import { create } from 'zustand';
import type {
  Conversation,
  Message,
  ServerMessage,
  LoadingPhase,
  ActiveTool,
  ServerInfo,
} from '../types';
import {
  listConversations,
  createConversation,
  getConversation,
  deleteConversation,
  updateConversation,
  listTools,
  ChatWebSocket,
} from '../api/client';
import { MCP_PRESETS } from '../config/presets';

interface ChatState {
  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;
  activeMessages: Message[];
  isLoadingConversations: boolean;

  // Chat
  isConnected: boolean;
  isWaiting: boolean;
  pendingMessage: string;
  ws: ChatWebSocket | null;

  // Loading status
  loadingPhase: LoadingPhase;
  activeTool: ActiveTool | null;

  // MCP Server selection
  availableServers: ServerInfo[];
  enabledServers: string[];
  isLoadingServers: boolean;
  serverError: string | null;
  activePreset: string | null; // Preset ID or null if custom

  // Model selection
  availableModels: string[];
  selectedModel: string | null;

  // Error state
  error: string | null;

  // Actions
  loadConversations: () => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  createNewConversation: (title?: string) => Promise<string>;
  removeConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, title: string) => Promise<void>;

  // Server/Model actions
  loadServers: () => Promise<void>;
  toggleServer: (serverName: string) => void;
  setPreset: (presetId: string) => void;
  setSelectedModel: (model: string) => void;

  // WebSocket actions
  connectToConversation: (id: string) => void;
  disconnectChat: () => void;
  sendMessage: (content: string) => void;

  // Internal
  handleServerMessage: (msg: ServerMessage) => void;
  clearError: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  conversations: [],
  activeConversationId: null,
  activeMessages: [],
  isLoadingConversations: false,
  isConnected: false,
  isWaiting: false,
  pendingMessage: '',
  ws: null,
  loadingPhase: 'idle',
  activeTool: null,
  availableServers: [],
  enabledServers: [],
  isLoadingServers: false,
  serverError: null,
  activePreset: 'minimal',
  availableModels: [],
  selectedModel: null,
  error: null,

  // Load conversations list
  loadConversations: async () => {
    set({ isLoadingConversations: true, error: null });
    try {
      const response = await listConversations();
      set({ conversations: response.conversations, isLoadingConversations: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoadingConversations: false });
    }
  },

  // Select and load a conversation
  selectConversation: async (id: string) => {
    const { ws, activeConversationId } = get();

    // Disconnect from previous if different
    if (activeConversationId !== id && ws) {
      ws.close();
      set({ ws: null, isConnected: false });
    }

    set({ activeConversationId: id, error: null });

    try {
      const data = await getConversation(id);
      set({ activeMessages: data.messages });

      // Connect WebSocket
      get().connectToConversation(id);
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  // Create a new conversation
  createNewConversation: async (title?: string) => {
    try {
      const conversation = await createConversation(title);
      const { conversations } = get();
      set({ conversations: [conversation, ...conversations] });
      return conversation.id;
    } catch (e) {
      set({ error: (e as Error).message });
      throw e;
    }
  },

  // Remove a conversation
  removeConversation: async (id: string) => {
    const { activeConversationId, ws } = get();

    try {
      await deleteConversation(id);

      // If deleting active conversation, clean up
      if (activeConversationId === id) {
        if (ws) ws.close();
        set({
          activeConversationId: null,
          activeMessages: [],
          ws: null,
          isConnected: false,
        });
      }

      // Remove from list
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== id),
      }));
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  // Rename a conversation
  renameConversation: async (id: string, title: string) => {
    try {
      const updated = await updateConversation(id, { title });
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, title: updated.title } : c
        ),
      }));
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  // Load available MCP servers
  loadServers: async () => {
    set({ isLoadingServers: true, serverError: null });
    try {
      const tools = await listTools();
      // Group tools by server
      const serverMap = new Map<string, string[]>();
      tools.forEach((t) => {
        const existing = serverMap.get(t.server) || [];
        serverMap.set(t.server, [...existing, t.name]);
      });

      const servers = Array.from(serverMap.entries()).map(([name, toolList]) => ({
        name,
        tools: toolList,
        enabled: true,
      }));

      const currentPresetId = get().activePreset ?? 'minimal';
      const preset = MCP_PRESETS.find((item) => item.id === currentPresetId);
      let enabledServers = servers.map((server) => server.name);

      if (preset) {
        if (preset.servers === '*') {
          enabledServers = servers.map((server) => server.name);
        } else {
          const available = new Set(servers.map((server) => server.name));
          enabledServers = preset.servers.filter((server) => available.has(server));
        }
      }

      set({
        availableServers: servers,
        enabledServers,
        isLoadingServers: false,
      });
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to load MCP servers';
      console.error('Failed to load servers:', e);
      set({ serverError: errorMsg, isLoadingServers: false });
    }
  },

  // Toggle a server on/off
  toggleServer: (serverName: string) => {
    set((state) => {
      const isEnabled = state.enabledServers.includes(serverName);
      return {
        enabledServers: isEnabled
          ? state.enabledServers.filter((s) => s !== serverName)
          : [...state.enabledServers, serverName],
        activePreset: null, // Clear preset when manually toggling
      };
    });
  },

  // Set a preset configuration
  setPreset: (presetId: string) => {
    const { availableServers } = get();
    const preset = MCP_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;

    let newEnabledServers: string[];
    if (preset.servers === '*') {
      // Enable all available servers
      newEnabledServers = availableServers.map((s) => s.name);
    } else {
      // Enable only servers that exist in available servers
      const available = new Set(availableServers.map((s) => s.name));
      newEnabledServers = (preset.servers as string[]).filter((s) => available.has(s));
    }

    set({
      enabledServers: newEnabledServers,
      activePreset: presetId,
    });
  },

  // Set selected model
  setSelectedModel: (model: string) => {
    set({ selectedModel: model });
  },

  // Connect WebSocket to conversation
  connectToConversation: (id: string) => {
    const { handleServerMessage } = get();

    const ws = new ChatWebSocket(
      id,
      handleServerMessage,
      () => set({ isConnected: false }),
      () => set({ error: 'WebSocket error', isConnected: false })
    );

    ws.connect();
    set({ ws, isConnected: false });
  },

  // Disconnect WebSocket
  disconnectChat: () => {
    const { ws } = get();
    if (ws) {
      ws.close();
      set({ ws: null, isConnected: false });
    }
  },

  // Send a message
  sendMessage: (content: string) => {
    const { ws, activeConversationId, activeMessages, enabledServers, selectedModel } = get();

    if (!ws || !activeConversationId) {
      set({ error: 'Not connected' });
      return;
    }

    // Add user message to UI immediately
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: activeConversationId,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };

    set({
      activeMessages: [...activeMessages, userMessage],
      isWaiting: true,
      pendingMessage: '',
      loadingPhase: 'thinking',
      activeTool: null,
    });

    // Send message with enabled servers
    ws.sendMessage(content, enabledServers, selectedModel ?? undefined);
  },

  // Handle server messages
  handleServerMessage: (msg: ServerMessage) => {
    const { activeMessages, activeConversationId } = get();

    switch (msg.type) {
      case 'connected':
        set({ isConnected: true });
        break;

      case 'token':
        // Streaming tokens - append to pending message
        set((state) => ({
          pendingMessage: state.pendingMessage + (msg.content || ''),
          loadingPhase: 'generating',
        }));
        break;

      case 'tool_start':
        // Tool execution started - show in UI
        set({
          loadingPhase: 'tool_calling',
          activeTool: { name: msg.name!, arguments: msg.arguments },
        });
        break;

      case 'tool_result':
        // Tool completed - back to thinking until next event
        set({
          loadingPhase: 'thinking',
          activeTool: null,
        });
        break;

      case 'message':
        // Complete message received
        if (msg.content && activeConversationId) {
          const assistantMessage: Message = {
            id: msg.id || `msg-${Date.now()}`,
            conversation_id: activeConversationId,
            role: 'assistant',
            content: msg.content,
            tool_calls: msg.tool_calls,
            tool_results: msg.tool_results,
            created_at: new Date().toISOString(),
          };

          set({
            activeMessages: [...activeMessages, assistantMessage],
            isWaiting: false,
            pendingMessage: '',
            loadingPhase: 'idle',
            activeTool: null,
          });
        }
        break;

      case 'error':
        set({
          error: msg.message || 'Unknown error',
          isWaiting: false,
          pendingMessage: '',
          loadingPhase: 'idle',
          activeTool: null,
        });
        break;

      case 'pong':
        // Keepalive response
        break;

      case 'conversation_updated':
        // Server emits this after auto-rename of the title (PR C). Upsert
        // into the local conversations array — no refetch needed.
        if (msg.conversation) {
          const conv = msg.conversation;
          set((state) => ({
            conversations: state.conversations.map((c) => (c.id === conv.id ? conv : c)),
          }));
        }
        break;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
